import React, { useState, useEffect } from "react";
import { Box, Card, CardHeader, CardContent, Chip, Grid, Typography, IconButton, Select, MenuItem, Switch, Tooltip } from "@mui/material";
import styled from "@emotion/styled";
import StatusChip from "./StatusChip";
import PriorityChip from "./PriorityChip";
import { Edit, Check, Close, CheckCircle } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateProject, updateProjectStatus } from "../redux/slices/project";
import { useHasPermission } from "../utils/permissionUtils";
import { useIsProjectLocked } from "../utils/lockUtils";
import AppDatePicker from "./AppDatePicker";
import { formatDate, formatDateTime } from "../utils/dateUtils";

const InfoCard = styled(Card)(({ theme }) => ({
    gridColumn: 'span 3',
    [theme.breakpoints.up('sm')]: {
        gridColumn: 'span 1',
    }
}));

const CardTitleText = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.h5.fontSize,
}));

const ContentGridRow = styled(Grid)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(2),
}));

const CardHeaderContent = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
}));

const DatePairRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(1),
}));

const DatePairCell = styled(Box)({
    flex: '1 1 140px',
    minWidth: 0,
});

const ButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
}));

const ProjectStatusPriority = () => {
    const dispatch = useDispatch();
    const hasPermission = useHasPermission();
    const canEditProjectDates = hasPermission('editProjectDates')
    const canEditCompleted = hasPermission('editCompletedProjects');
    const canSetProjectBilling = hasPermission('setProjectBilling');
    const project = useSelector(state => state.project.currentProject);
    const isLocked = useIsProjectLocked(project);
    const [isEditing, setIsEditing] = useState(false);
    const [localStatus, setLocalStatus] = useState(project.status);
    const [localPriority, setLocalPriority] = useState(project.priority);
    const [localStartDate, setLocalStartDate] = useState(project.startDate);
    const [localDueDate, setLocalDueDate] = useState(project.dueDate);
    const [localRush, setLocalRush] = useState(!!project.rush);

    useEffect(() => {
        setLocalStatus(project.status);
        setLocalPriority(project.priority);
        setLocalStartDate(project.startDate);
        setLocalDueDate(project.dueDate);
        setLocalRush(!!project.rush);
    }, [project]);

    const handleEdit = () => setIsEditing(true);

    const handleCancel = () => {
        setIsEditing(false);
        setLocalStatus(project.status);
        setLocalPriority(project.priority);
        setLocalStartDate(project.startDate);
        setLocalDueDate(project.dueDate);
        setLocalRush(!!project.rush);
    };

    const handleSave = () => {
        if (isLocked) return;
        const projectData = {
            priority: localPriority,
            startDate: localStartDate,
            dueDate: localDueDate,
        };
        if (canSetProjectBilling) projectData.rush = localRush;
        dispatch(updateProject({ projectId: project._id, projectData }));
        setIsEditing(false);
    };

    const handleChange = (field) => (event) => {
        const value = event.target.value;
        switch (field) {
            case 'priority':
                setLocalPriority(value);
                break;
            case 'startDate':
                setLocalStartDate(value);
                if (new Date(value) > new Date(localDueDate)) {
                    setLocalDueDate(value);
                }
                break;
            case 'dueDate':
                setLocalDueDate(value);
                break;
            default:
                break;
        }
    };

    const handleStatusChange = (projectId, newStatus) => {
        if (isLocked) return;
        dispatch(updateProjectStatus({ projectId, status: newStatus }));
    }

    const getStatusProps = () => {
        if (project.completedDate) {
            return { status: 'Completed', remainingDays: 0 };
        }
        return { status: localStatus, remainingDays: project.remainingDays };
    };

    return (
        <InfoCard>
            <CardHeader
                title={
                    <CardHeaderContent>
                        <CardTitleText>Status & Priority</CardTitleText>
                        {(project.completedDate === null || canEditCompleted) && canEditProjectDates && !isLocked && (
                            <ButtonGroup>
                                {!isEditing ? (
                                    <IconButton onClick={handleEdit}>
                                        <Edit />
                                    </IconButton>
                                ) : (
                                    <>
                                        <IconButton onClick={handleSave}>
                                            <Check />
                                        </IconButton>
                                        <IconButton onClick={handleCancel}>
                                            <Close />
                                        </IconButton>
                                    </>
                                )}
                            </ButtonGroup>
                        )}
                    </CardHeaderContent>
                }
            />
            <CardContent>
                <ContentGridRow>
                    <Typography>Status</Typography>
                    <StatusChip id={project._id} {...getStatusProps()} parentType="project" onChange={handleStatusChange} />
                </ContentGridRow>
                <ContentGridRow>
                    <Typography>Priority</Typography>
                    {!isEditing ? (
                        <PriorityChip priority={localPriority} />
                    ) : (
                        <Select
                            value={localPriority || ''}
                            onChange={handleChange('priority')}
                            size="small"
                        >
                            {['Lowest', 'Low', 'Medium', 'High', 'Highest'].map((priority) => (
                                <MenuItem key={priority} value={priority}>{priority}</MenuItem>
                            ))}
                        </Select>
                    )}
                </ContentGridRow>
                <ContentGridRow>
                    <Typography>Rush job</Typography>
                    {!(isEditing && canSetProjectBilling) ? (
                        <Chip
                            size="small"
                            icon={localRush ? <CheckCircle fontSize="small" /> : undefined}
                            color={localRush ? "error" : "default"}
                            label={localRush ? "Yes" : "No"}
                        />
                    ) : (
                        <Tooltip title="Mark this project as a rush job">
                            <Switch
                                checked={localRush}
                                onChange={(event) => setLocalRush(event.target.checked)}
                                color="primary"
                                disabled={isLocked}
                                inputProps={{ 'aria-label': 'Rush job' }}
                            />
                        </Tooltip>
                    )}
                </ContentGridRow>
                <DatePairRow>
                    {(project.completedDate === null || canEditCompleted) &&
                        <DatePairCell>
                            {!isEditing ? (
                                <>
                                    <Typography variant="body2">Start Date</Typography>
                                    <Typography variant="subtitle2">{formatDate(localStartDate)}</Typography>
                                </>
                            ) : (
                                <AppDatePicker
                                    label="Start Date"
                                    value={formatDate(localStartDate)}
                                    onChange={(v) => handleChange('startDate')({ target: { value: v } })}
                                    size="small"
                                />
                            )}
                        </DatePairCell>
                    }
                    <DatePairCell>
                        {!isEditing ? (
                            <>
                                <Typography variant="body2">Due Date</Typography>
                                <Typography variant="subtitle2">{formatDate(localDueDate)}</Typography>
                            </>
                        ) : (
                            <AppDatePicker
                                label="Due Date"
                                value={formatDate(localDueDate)}
                                onChange={(v) => handleChange('dueDate')({ target: { value: v } })}
                                size="small"
                                minDate={formatDate(localStartDate)}
                            />
                        )}
                    </DatePairCell>
                    {project.completedDate !== null &&
                        <DatePairCell>
                            <Typography variant="body2">Completed Date</Typography>
                            <Typography variant="subtitle2">{formatDate(project.completedDate)}</Typography>
                        </DatePairCell>
                    }
                </DatePairRow>
                <DatePairRow>
                    <DatePairCell>
                        <Typography variant="body2">Created</Typography>
                        <Typography variant="subtitle2">{formatDateTime(project.createdAt)}</Typography>
                    </DatePairCell>
                    <DatePairCell>
                        <Typography variant="body2">Last Updated</Typography>
                        <Typography variant="subtitle2">{formatDateTime(project.updatedAt)}</Typography>
                    </DatePairCell>
                </DatePairRow>
            </CardContent>
        </InfoCard>
    );
};

export default ProjectStatusPriority;